import type { StateCreator } from 'zustand';
import { type AppStore } from '../store';
import { calculateRating } from 'api/calculator';
import type { CoveragePremium } from 'dto/CoveragePremium';
import type { EditableFormValues } from 'individual/components/BackgroundInfo/EditableForm';
import type { BackgroundInfoForm } from 'individual/types/backgroundInfoForm';
import type { PurchasePathState } from 'individual/types/initialState';
import { getEmptyBackground } from 'state/state';
import { getInitialState } from 'utils';

/**
 * Determines the initial background information form based on authentication and calculator input state.
 *
 * @returns BackgroundInfoForm initialized based on:
 * - Unauthenticated user: empty background form
 * - Authenticated without calculatorInput: partyInformation.dateOfBirth + empty background
 * - Authenticated with calculatorInput: partyInformation.dateOfBirth + calculatorInput.backgroundInfo
 */
export function getInitialBackgroundInfo(): BackgroundInfoForm {
  const { authenticatedCustomer, partyInformation, calculatorInput } =
    getInitialState();

  // Case 1: Unauthenticated user
  if (!authenticatedCustomer) {
    return getEmptyBackground();
  }

  // Case 2 & 3: Authenticated user - always override dateOfBirth with partyInformation
  const dateOfBirth = partyInformation?.dateOfBirth ?? '';

  // Case 3: Use calculatorInput.backgroundInfo if available
  if (calculatorInput?.backgroundInfo) {
    return {
      ...calculatorInput.backgroundInfo,
      dateOfBirth,
    };
  }

  // Case 2: No calculatorInput, use empty background with dateOfBirth
  return {
    ...getEmptyBackground(),
    dateOfBirth,
  };
}

/**
 * Get initial state for Form slice
 * @returns Initial form state combining background info and loading state
 */
export const getInitialFormState = () => ({
  backgroundInfoForm: getInitialBackgroundInfo(),
  ratingLoading: false,
});

/**
 * Form Slice - Manages background information state.
 *
 * This slice handles user's background information including date of birth,
 * income, debt, children count, smoking status, and working status,
 * as well as rating operations.
 */
export interface FormSlice {
  /**
   * Current background information form data
   */
  backgroundInfoForm: BackgroundInfoForm;

  /**
   * Loading state for rating calculation
   */
  ratingLoading: boolean;

  /**
   * Update the background information form
   * @param form - The new background information form data
   */
  setBackgroundInfoForm: (form: BackgroundInfoForm) => void;

  /**
   * Update the rating loading state
   * @param loading - The new loading state
   */
  setRatingLoading: (loading: boolean) => void;

  /**
   * Submit background info and calculate rating
   * @param formValues - The form values to submit
   * @returns The coverage premiums from the rating calculation
   */
  submitBackgroundInfo: (
    formValues: EditableFormValues
  ) => Promise<CoveragePremium[]>;
}

/**
 * Creates the Form slice with background info state management.
 *
 * Note: Initial state is determined at store creation based on server-rendered data:
 * - Unauthenticated: empty forms with defaults
 * - Authenticated: partyInformation.dateOfBirth + calculatorInput (if present)
 */
export const createFormSlice: StateCreator<
  AppStore,
  [['zustand/devtools', never]],
  [],
  FormSlice
> = (set, get) => ({
  ...getInitialFormState(),

  setBackgroundInfoForm: (form: BackgroundInfoForm) => {
    set({ backgroundInfoForm: form }, false, 'formSlice/setBackgroundInfoForm');
  },

  setRatingLoading: (loading: boolean) => {
    set({ ratingLoading: loading }, false, 'formSlice/setRatingLoading');
  },

  submitBackgroundInfo: async (
    formValues: EditableFormValues
  ): Promise<CoveragePremium[]> => {
    set({ ratingLoading: true }, false, 'formSlice/submitBackgroundInfo/start');

    const {
      marketableProduct: { name },
    } = getInitialState() as PurchasePathState;

    let coveragePremiums: CoveragePremium[] = [];
    try {
      const res = await calculateRating(name, formValues);
      coveragePremiums = res.coveragePremiums;
      // Update coverage premiums via CoverageSlice (cross-slice coordination)
      // Use type assertion to access other slice methods
      const storeState = get();
      storeState.setCoveragePremiums(coveragePremiums);
    } finally {
      set(
        { ratingLoading: false },
        false,
        'formSlice/submitBackgroundInfo/end'
      );
    }
    return coveragePremiums;
  },
});
